import { FC, useContext, useState, useEffect, useRef } from "react";
import { useDebouncedCurrentGame } from "../../../hooks/useDebouncedCurrentGame";
import { CompetitionContext } from "../../../CompetitionContext.tsx";
import { useTeam } from "../../../hooks/useTeam.ts";
import { usePlayer } from "../../../hooks/usePlayer.ts";
import { getPresentationParamsForOrigin } from "../../../getPresentationParamsForOrigin.ts";
import { PlayerName } from "../../../Components/PlayerName.tsx";
import { PlayerNumber } from "../../../Components/PlayerNumber.tsx";
import { TeamLogo } from "../../../Components/TeamLogo.tsx";

const GoalScorerContent: FC<{
  playerId: string;
  teamId: string | undefined;
  isOwnGoal: boolean;
}> = ({ playerId, teamId, isOwnGoal }) => {
  const playerData = usePlayer(playerId);
  const teamData = useTeam(teamId);

  if (!playerData.isSuccess || !teamData.isSuccess) {
    return null;
  }

  // Check if color is too light (close to white)
  const isLightColor = (hex: string) => {
    const color = hex.replace("#", "");
    const r = parseInt(color.substring(0, 2), 16);
    const g = parseInt(color.substring(2, 4), 16);
    const b = parseInt(color.substring(4, 6), 16);
    // Calculate perceived brightness
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness > 200;
  };

  const numberColor = isLightColor(teamData.data.primaryColor)
    ? "black"
    : teamData.data.primaryColor;

  return (
    <div style={{ display: "flex", justifyContent: "center" }}>
      <div
        style={{
          display: "inline-block",
          position: "relative",
          top: 900,
          fontFamily: "Chivo",
          fontWeight: 700,
          backgroundColor: "white",
          color: "black",
        }}
      >
        <div style={{ display: "flex" }}>
          {/* Label: TOR / EIGENTOR */}
          <div
            style={{
              height: 80,
              width: isOwnGoal ? 180 : 140,
              lineHeight: "80px",
              textAlign: "center",
              backgroundColor: getPresentationParamsForOrigin().colors.brand[9],
              color: "white",
              fontSize: 28,
            }}
          >
            {isOwnGoal ? "EIGENTOR" : "TOR"}
          </div>

          {/* Team Logo */}
          <div
            style={{
              height: 80,
              display: "flex",
              alignItems: "center",
              borderLeftWidth: 12,
              borderLeftStyle: "solid",
              borderLeftColor: teamData.data.primaryColor,
              paddingLeft: 20,
              paddingRight: 20,
            }}
          >
            <TeamLogo teamId={teamData.data.id} height={50} />
          </div>

          {/* Player Name and Number */}
          <div
            style={{
              height: 80,
              display: "flex",
              alignItems: "center",
              paddingLeft: 20,
              paddingRight: 40,
              fontSize: 32,
            }}
          >
            <span style={{ color: numberColor, marginRight: 12 }}>
              <PlayerNumber playerId={playerId} />
            </span>
            <PlayerName variant={"firstname lastname"} playerId={playerId} />
          </div>
        </div>
      </div>
    </div>
  );
};

export const GoalScorer: FC = () => {
  const competitionId = useContext(CompetitionContext);
  const currentGameData = useDebouncedCurrentGame(competitionId, 0);
  const [showFlash, setShowFlash] = useState(false);
  const prevStateRef = useRef<{
    goalCount: number;
    lastGoalHasScorer: boolean;
  } | null>(null);

  const goals = currentGameData.data?.goals ?? [];
  const lastGoal = goals[goals.length - 1];
  const lastGoalHasScorer = !!lastGoal?.playerId;

  useEffect(() => {
    if (!currentGameData.isSuccess) return;

    const currentGoalCount = goals.length;
    const prevState = prevStateRef.current;

    // Flash when:
    // 1. New goal added that already has a scorer, OR
    // 2. Scorer was just assigned to the last goal
    const shouldFlash =
      prevState !== null &&
      lastGoalHasScorer &&
      (currentGoalCount > prevState.goalCount || !prevState.lastGoalHasScorer);

    if (shouldFlash) {
      setShowFlash(true);
      const timer = setTimeout(() => {
        setShowFlash(false);
      }, 500);
      return () => {
        clearTimeout(timer);
      };
    }

    prevStateRef.current = { goalCount: currentGoalCount, lastGoalHasScorer };
  }, [currentGameData.isSuccess, goals.length, lastGoalHasScorer]);

  if (!currentGameData.isSuccess) {
    return null;
  }

  if (!lastGoal) {
    return null;
  }

  // Determine which team scored
  const scoringTeamId = lastGoal.homeTeam
    ? currentGameData.data.actualTeams.homeTeamId
    : currentGameData.data.actualTeams.guestTeamId;

  return (
    <>
      {/* Red flash overlay */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundColor: "red",
          opacity: showFlash ? 0.75 : 0,
          transition: "opacity 0.3s ease-out",
          pointerEvents: "none",
          zIndex: 9999,
        }}
      />
      {/* Only show scorer content if playerId is set */}
      {lastGoal.playerId && (
        <GoalScorerContent
          playerId={lastGoal.playerId}
          teamId={scoringTeamId}
          isOwnGoal={lastGoal.isOwnGoal}
        />
      )}
    </>
  );
};
